import React, { useEffect, useState } from 'react';
import { C, Card, Spinner, Alert } from '../../components/UI';
import { useAuth } from '../../context/AuthContext';
import { useBranding } from '../../context/BrandingContext';
import { api, ClientPortalPropertyDto, PropertyHistoryItemDto } from '../../services/api';

// ── Client Portal (core infrastructure) ─────────────────────────────────────────
// Read-only external login for the tenant's own B2B client (e.g. a mortgage servicer/investor)
// to see their own properties — see ClientUser's doc comment in Models.cs. Deliberately simple:
// no SignalR, no write actions, just a property list + history. The role/portal shell itself is
// generic core plumbing; content comes entirely from whatever paid app populates properties/jobs.

const occupancyColors: Record<string, [string, string]> = {
    Occupied: [C.greenBg, C.greenMid],
    Vacant: ['#FEF3C7', '#92400E'],
    Unknown: ['#F5F5F5', C.textMuted],
};

export default function ClientPortal() {
    const { user, logout } = useAuth();
    const branding = useBranding();
    const [properties, setProperties] = useState<ClientPortalPropertyDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState<ClientPortalPropertyDto | null>(null);

    useEffect(() => {
        api.getMyClientProperties()
            .then(setProperties)
            .catch(e => setError(e.message || 'Failed to load properties.'))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'Inter', sans-serif" }}>
            <div style={{ background: branding.primaryColor, padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    {branding.logoUrl
                        ? <img src={branding.logoUrl} alt={branding.appName} style={{ height: 32, maxWidth: 120, objectFit: 'contain' }} />
                        : <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontStyle: 'italic', fontSize: 16, letterSpacing: 1, color: branding.navTextColor ?? '#ffffff' }}>
                            {branding.appName}
                          </span>
                    }
                    <span style={{ color: branding.navTextColor ?? '#ffffff', fontSize: 13, opacity: 0.7 }}>· {user?.name || 'Client Portal'}</span>
                </div>
                <button onClick={logout} style={{ background: 'none', border: '1px solid var(--color-accent, #4ADE80)', color: 'var(--color-accent, #4ADE80)', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>Sign out</button>
            </div>

            <div style={{ maxWidth: 800, margin: '0 auto', padding: 24 }}>
                <Alert message={error} />
                {selected ? (
                    <PropertyDetail property={selected} onBack={() => setSelected(null)} />
                ) : (
                    <>
                        <h2 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 700, color: C.text }}>Your Properties</h2>
                        {loading ? <Spinner /> : properties.length === 0 ? (
                            <Card style={{ textAlign: 'center', padding: 48, color: C.textMuted }}>No properties have been shared with you yet.</Card>
                        ) : (
                            <Card style={{ padding: 0, overflow: 'hidden' }}>
                                {properties.map((p, i) => {
                                    const [bg, fg] = occupancyColors[p.occupancyStatus] || occupancyColors.Unknown;
                                    return (
                                        <div key={p.id} onClick={() => setSelected(p)} style={{
                                            display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', cursor: 'pointer',
                                            borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
                                        }}>
                                            <span style={{
                                                fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99,
                                                background: bg, color: fg, flexShrink: 0, minWidth: 72, textAlign: 'center',
                                            }}>{p.occupancyStatus}</span>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{p.address}</div>
                                                <div style={{ fontSize: 12, color: C.textSub }}>{[p.city, p.state].filter(Boolean).join(', ')}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </Card>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function PropertyDetail({ property, onBack }: { property: ClientPortalPropertyDto; onBack: () => void }) {
    const [history, setHistory] = useState<PropertyHistoryItemDto[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.getMyClientPropertyHistory(property.id).then(setHistory).catch(() => {}).finally(() => setLoading(false));
    }, [property.id]);

    return (
        <div>
            <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.greenMid, cursor: 'pointer', fontSize: 13, marginBottom: 16, padding: 0 }}>← Back to properties</button>
            <Card style={{ padding: 24, marginBottom: 16 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{property.address}</div>
                <div style={{ fontSize: 13, color: C.textSub, marginTop: 4 }}>{[property.city, property.state].filter(Boolean).join(', ')} · {property.occupancyStatus}</div>
            </Card>

            <Card style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, fontSize: 14, fontWeight: 700, color: C.text }}>Job history</div>
                {loading ? <Spinner /> : history.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', color: C.textMuted }}>No jobs recorded at this property yet.</div>
                ) : (
                    history.map((h, i) => (
                        <div key={h.jobId} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '12px 18px', borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
                        }}>
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{h.serviceTypeName}</div>
                                <div style={{ fontSize: 12, color: C.textMuted }}>{h.status}</div>
                            </div>
                            <div style={{ fontSize: 12, color: C.textSub }}>
                                {h.completedAt ? `Completed ${new Date(h.completedAt).toLocaleDateString()}`
                                    : h.scheduledDate ? `Scheduled ${new Date(h.scheduledDate).toLocaleDateString()}` : '—'}
                            </div>
                        </div>
                    ))
                )}
            </Card>
        </div>
    );
}
